import { c as _c } from "react/compiler-runtime";
import { CopilotKit, type JsonSerializable, type Message as AgUiMessage, useFrontendTool as useCopilotKitFrontendTool } from '@copilotkit/react-core/v2';
import { type CopilotErrorEvent } from '@copilotkit/shared';
import { useTheme } from '@precis-digital/kurama/utils';
import { createContext, type ReactElement, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { makeErrorToast } from '../components/Toaster';
import { useAuth } from 'shared/context/AuthContext';
import { useCurrentClient } from 'shared/context/ClientContext';
import { useCopilotSession } from 'shared/copilot/hooks/useCopilotSession';
import { useCopilotAgentContext } from 'shared/copilot/hooks/useCopilotAgentContext';
import { type CopilotSendToolResultArgs, type JustSentImagePreview, type SendUserMessageOptions, useCopilotAgentConversation } from 'shared/copilot/hooks/useCopilotAgentConversation';
import { type AttachedCsvFile, type CsvAttachmentStatus } from 'shared/copilot/hooks/useCopilotCsvAttachment';
import { type CopilotImageAttachment, type ImageAttachmentError } from 'shared/copilot/hooks/useCopilotImageAttachments';
import { CSV_LIMIT_COPY_VALUES, type CsvValidationReason } from 'shared/copilot/utils/csvAttachment';
import { IMAGE_LIMIT_COPY_VALUES } from 'shared/copilot/utils/imageAttachment';
import { BackendLongRunningToolStubs } from 'shared/copilot/hooks/BackendLongRunningToolStubs';
import { FEATURE_FLAGS, useFeatureFlag } from 'shared/featureFlag';
import { useTranslation } from 'shared/translations';
import { type CopilotFrontendToolElementRef, type CopilotFrontendToolFn, useCopilotFrontendTool } from 'shared/copilot/hooks/useCopilotFrontendTool';
import { pulseCopilotFrontendToolElementRefs, resetCopilotFrontendToolElementEffectState, useCopilotFrontendToolElementEffects } from 'shared/copilot/hooks/useCopilotFrontendToolElementEffects';
import { ALVIE_COPILOT_AGENT_ID, type CopilotAgentName, type CopilotSurface, IS_AGENT_SWITCHER_ENABLED, isCopilotAgentName, SURFACE_DEFAULT_AGENT, toRuntimeAgentId } from 'shared/copilot/copilotSurfaces';
import { useDrawerChatAgent } from 'shared/copilot/hooks/useDrawerChatAgent';
import { type FlowDatasourceEntityType } from 'shared/copilot/flowDataAgentConstants';
import { type CirrusSourceOption } from 'shared/copilot/cirrusSource';
import { getCopilotBadRequestMessage, isCopilotSessionMissing } from 'shared/copilot/utils/copilotErrorMessage';
import { ApiClientError } from 'shared/api/fetch';
import { AUTH_SESSION_MISSING, SESSION_EXPIRED_MESSAGE } from 'shared/api/authSession';
import { dispatchClientError } from 'shared/components/ApiClienErrorHandler';
import { HttpStatusCodes } from 'shared/httpStatus';
import { type AlvieDatasourceOption } from 'shared/copilot/utils/alvieDatasourceOptions';
import { type CorpusResponse } from 'shared/api/copilot';
import { scrollCopilotElementIntoView } from 'shared/copilot/utils/scrollCopilotElementIntoView';
import type { CopilotPageContext } from '../copilot/pageContext/types';
import { toCopilotAgentPageContext } from '../copilot/pageContext/toCopilotAgentPageContext';
import { useCopilotGlobalAgentContext } from 'shared/copilot/globalContext/useCopilotGlobalAgentContext';
import type { CopilotActionDisplayResolvers } from 'shared/copilot/toolRegistry/types';
import { useCopilotSurface } from 'shared/copilot/CopilotSurfaceContext';
import { useCopilotUrlStateMirror } from 'shared/copilot/hooks/useCopilotUrlStateMirror';
import { parseCopilotUrlState } from 'shared/copilot/utils/copilotUrlState';
import logger from 'shared/logger';
import { get, LOCAL_STORAGE_KEYS, store } from 'shared/lstore';
export type { CopilotSendToolResultArgs };
const log = logger().child({
  namespace: 'shared/context/CopilotContext'
});
interface CopilotConversationFields {
  messages: AgUiMessage[];
  sendUserMessage: (message: string, options?: SendUserMessageOptions) => boolean;
  sendToolResult: (args: CopilotSendToolResultArgs) => void;
  isLoading: boolean;
  isHydratingChat: boolean;
  chatLoadFailed: boolean;
  retryChatLoad: () => void;
  stopGeneration: () => void;
  currentChatId: string | null;
  createNewChat: (options?: {
    initialMessage?: string;
  }) => void;
  loadChat: (chatId: string) => void;
  selectedDatasourceIds: string[];
  selectedEntityTypes: FlowDatasourceEntityType[];
  selectedCirrusSource: CirrusSourceOption | null;
  hasSelectedDatasource: boolean;
  setSelectedDatasources: (optionIds: string[], datasourceOptions: AlvieDatasourceOption[]) => void;
  setSelectedCirrusSource: (option: CirrusSourceOption | null) => void;
  clearSelectedDatasource: (optionId?: string) => void;
  reconcileDatasourceWithAvailableOptions: (datasourceOptions: AlvieDatasourceOption[], cirrusOptions: readonly CirrusSourceOption[]) => void;
  selectedKnowledgeSourceIds: string[];
  hasSelectedKnowledgeSource: boolean;
  setSelectedKnowledgeSources: (corpusIds: string[], corpora: CorpusResponse[]) => void;
  clearSelectedKnowledgeSource: (corpusId?: string) => void;
  reconcileKnowledgeSourcesWithAvailableCorpora: (corpora: CorpusResponse[]) => void;
  attachedCsvFile: AttachedCsvFile | null;
  pendingCsvFilename: string | null;
  csvAttachmentStatus: CsvAttachmentStatus;
  csvAttachmentErrorReason: CsvValidationReason | 'uploadFailed' | null;
  isCsvUploading: boolean;
  attachCsvFile: (file: File) => Promise<void>;
  retryCsvAttachment: () => Promise<void>;
  clearCsvAttachment: () => void;
  dismissCsvAttachmentError: () => void;
  imageAttachments: CopilotImageAttachment[];
  imageAttachmentErrorReason: ImageAttachmentError | null;
  isImageUploading: boolean;
  attachImageFiles: (files: readonly File[]) => Promise<void>;
  removeImageAttachment: (id: string) => void;
  retryImageAttachment: (id: string) => Promise<void>;
  dismissImageAttachmentError: () => void;
  justSentImagePreviewByGcsUri: Readonly<Record<string, JustSentImagePreview>>;
}
interface CopilotShellFields {
  /** True when either surface flag is on and the provider is live. */
  isCopilotEnabled: boolean;
  /** Statsig `copilot-sidenav` — drawer Alvie AI. */
  isCopilotSidenavEnabled: boolean;
  /** Statsig `copilot` — fullscreen Alvie AI Pro. */
  isCopilotFullscreenEnabled: boolean;
  isCopilotOpen: boolean;
  openCopilotDrawer: () => void;
  closeCopilotDrawer: () => void;
  toggleCopilot: () => void;
  useCopilotFrontendTool: CopilotFrontendToolFn;
  currentPageContext: CopilotPageContext | null;
  registerPageContext: (pageContext: CopilotPageContext) => void;
  clearPageContext: (pageId: string) => void;
  registerPageElementRefs: (pageId: string, elementRefsById: CopilotPageElementRefsById) => () => void;
  highlightPageElement: (elementId: string) => boolean;
  scrollToPageElement: (elementId: string) => void;
  actionDisplayResolvers: CopilotActionDisplayResolvers;
  registerActionDisplayResolvers: (ownerId: string, resolvers: CopilotActionDisplayResolvers) => void;
  unregisterActionDisplayResolvers: (ownerId: string) => void;
}
export type CopilotContextProps = CopilotShellFields & CopilotConversationFields;
interface SurfaceAgentFields {
  /** Agent the surface's conversation is bound to (the open chat's agent). */
  agentName: CopilotAgentName;
  setAgentName: (agentName: CopilotAgentName) => void;
  /** Agent new chats start on (dev switcher choice, else the surface default). */
  preferredAgentName: CopilotAgentName;
  setPreferredAgentName: (agentName: CopilotAgentName) => void;
}
interface CopilotInternalContextValue extends CopilotShellFields {
  conversations: Record<CopilotSurface, CopilotConversationFields>;
  agents: Record<CopilotSurface, SurfaceAgentFields>;
}
const CopilotContext = createContext<CopilotInternalContextValue | undefined>(undefined);
export type CopilotPageElementRefsById = Partial<Record<string, CopilotFrontendToolElementRef[]>>;
const noop = (): void => {};
const noopSendUserMessage = (): boolean => false;
const noopCopilotFrontendTool: CopilotFrontendToolFn = (..._args) => undefined as ReturnType<CopilotFrontendToolFn>;
const noopRegisterPageContext = (_pageContext: CopilotPageContext): void => {};
const noopClearPageContext = (_pageId: string): void => {};
const noopRegisterPageElementRefs = (_pageId: string, _elementRefsById: CopilotPageElementRefsById): (() => void) => noop;
const noopHighlightPageElement = (_elementId: string): boolean => false;
const noopScrollToPageElement = (_elementId: string): void => {};
const noopRegisterActionDisplayResolvers = (_ownerId: string, _resolvers: CopilotActionDisplayResolvers): void => {};
const noopUnregisterActionDisplayResolvers = (_ownerId: string): void => {};
const noopConversation: CopilotConversationFields = {
  messages: [],
  sendUserMessage: noopSendUserMessage,
  sendToolResult: noop,
  isLoading: false,
  isHydratingChat: false,
  chatLoadFailed: false,
  retryChatLoad: noop,
  stopGeneration: noop,
  currentChatId: null,
  createNewChat: noop,
  loadChat: noop,
  selectedDatasourceIds: [],
  selectedEntityTypes: [],
  selectedCirrusSource: null,
  hasSelectedDatasource: false,
  setSelectedDatasources: noop,
  setSelectedCirrusSource: noop,
  clearSelectedDatasource: noop,
  reconcileDatasourceWithAvailableOptions: noop,
  selectedKnowledgeSourceIds: [],
  hasSelectedKnowledgeSource: false,
  setSelectedKnowledgeSources: noop,
  clearSelectedKnowledgeSource: noop,
  reconcileKnowledgeSourcesWithAvailableCorpora: noop,
  attachedCsvFile: null,
  pendingCsvFilename: null,
  csvAttachmentStatus: 'idle',
  csvAttachmentErrorReason: null,
  isCsvUploading: false,
  attachCsvFile: async () => undefined,
  retryCsvAttachment: async () => undefined,
  clearCsvAttachment: noop,
  dismissCsvAttachmentError: noop,
  imageAttachments: [],
  imageAttachmentErrorReason: null,
  isImageUploading: false,
  attachImageFiles: async () => undefined,
  removeImageAttachment: noop,
  retryImageAttachment: async () => undefined,
  dismissImageAttachmentError: noop,
  justSentImagePreviewByGcsUri: {}
};
const mergeCopilotActionDisplayResolvers = (resolversByOwner: Map<string, CopilotActionDisplayResolvers>): CopilotActionDisplayResolvers => {
  const merged: CopilotActionDisplayResolvers = {};
  resolversByOwner.forEach(resolvers => {
    if (resolvers.getMetricLabel != null) {
      merged.getMetricLabel = resolvers.getMetricLabel;
    }
  });
  return merged;
};
const disabledCopilotContextValue: CopilotInternalContextValue = {
  isCopilotEnabled: false,
  isCopilotSidenavEnabled: false,
  isCopilotFullscreenEnabled: false,
  isCopilotOpen: false,
  openCopilotDrawer: noop,
  closeCopilotDrawer: noop,
  toggleCopilot: noop,
  useCopilotFrontendTool: noopCopilotFrontendTool,
  currentPageContext: null,
  registerPageContext: noopRegisterPageContext,
  clearPageContext: noopClearPageContext,
  registerPageElementRefs: noopRegisterPageElementRefs,
  highlightPageElement: noopHighlightPageElement,
  scrollToPageElement: noopScrollToPageElement,
  actionDisplayResolvers: {},
  registerActionDisplayResolvers: noopRegisterActionDisplayResolvers,
  unregisterActionDisplayResolvers: noopUnregisterActionDisplayResolvers,
  conversations: {
    drawer: noopConversation,
    fullscreen: noopConversation
  },
  agents: {
    drawer: disabledSurfaceAgent('drawer'),
    fullscreen: disabledSurfaceAgent('fullscreen')
  }
};
function disabledSurfaceAgent(surface: CopilotSurface): SurfaceAgentFields {
  const defaultAgentName = SURFACE_DEFAULT_AGENT[surface];
  return {
    agentName: defaultAgentName,
    setAgentName: noop,
    preferredAgentName: defaultAgentName,
    setPreferredAgentName: noop
  };
}
const readPreferredAgentName = (surface: CopilotSurface): CopilotAgentName => {
  const defaultAgentName = SURFACE_DEFAULT_AGENT[surface];
  if (!IS_AGENT_SWITCHER_ENABLED) {
    return defaultAgentName;
  }
  const stored = get(LOCAL_STORAGE_KEYS.COPILOT_AGENT_NAME(surface));
  return isCopilotAgentName(stored) ? stored : defaultAgentName;
};
const useSurfaceAgentState = surface => {
  const $ = _c(8);
  let t0;
  if ($[0] !== surface) {
    t0 = () => readPreferredAgentName(surface);
    $[0] = surface;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [preferredAgentName, setPreferredAgentNameState] = useState(t0);
  const [agentName, setAgentName] = useState(preferredAgentName);
  let t1;
  if ($[2] !== surface) {
    t1 = name => {
      store(LOCAL_STORAGE_KEYS.COPILOT_AGENT_NAME(surface), name);
      setPreferredAgentNameState(name);
    };
    $[2] = surface;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const setPreferredAgentName = t1;
  let t2;
  if ($[4] !== agentName || $[5] !== preferredAgentName || $[6] !== setPreferredAgentName) {
    t2 = {
      agentName,
      setAgentName,
      preferredAgentName,
      setPreferredAgentName
    };
    $[4] = agentName;
    $[5] = preferredAgentName;
    $[6] = setPreferredAgentName;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};

/**
 * Public copilot API. Conversation fields resolve to the nearest
 * {@link CopilotSurfaceProvider} (default: drawer).
 */
export const useCopilot = () => {
  const $ = _c(19);
  const copilotContext = useContext(CopilotContext);
  const surface = useCopilotSurface();
  if (copilotContext == null) {
    throw new Error("No copilotContext.Provider found when calling copilotContext.");
  }
  const conversation = copilotContext.conversations[surface];
  let t0;
  if ($[0] !== conversation || $[1] !== copilotContext.actionDisplayResolvers || $[2] !== copilotContext.clearPageContext || $[3] !== copilotContext.closeCopilotDrawer || $[4] !== copilotContext.currentPageContext || $[5] !== copilotContext.highlightPageElement || $[6] !== copilotContext.isCopilotEnabled || $[7] !== copilotContext.isCopilotFullscreenEnabled || $[8] !== copilotContext.isCopilotOpen || $[9] !== copilotContext.isCopilotSidenavEnabled || $[10] !== copilotContext.openCopilotDrawer || $[11] !== copilotContext.registerActionDisplayResolvers || $[12] !== copilotContext.registerPageContext || $[13] !== copilotContext.registerPageElementRefs || $[14] !== copilotContext.scrollToPageElement || $[15] !== copilotContext.toggleCopilot || $[16] !== copilotContext.unregisterActionDisplayResolvers || $[17] !== copilotContext.useCopilotFrontendTool) {
    t0 = {
      isCopilotEnabled: copilotContext.isCopilotEnabled,
      isCopilotSidenavEnabled: copilotContext.isCopilotSidenavEnabled,
      isCopilotFullscreenEnabled: copilotContext.isCopilotFullscreenEnabled,
      isCopilotOpen: copilotContext.isCopilotOpen,
      openCopilotDrawer: copilotContext.openCopilotDrawer,
      closeCopilotDrawer: copilotContext.closeCopilotDrawer,
      toggleCopilot: copilotContext.toggleCopilot,
      useCopilotFrontendTool: copilotContext.useCopilotFrontendTool,
      currentPageContext: copilotContext.currentPageContext,
      registerPageContext: copilotContext.registerPageContext,
      clearPageContext: copilotContext.clearPageContext,
      registerPageElementRefs: copilotContext.registerPageElementRefs,
      highlightPageElement: copilotContext.highlightPageElement,
      scrollToPageElement: copilotContext.scrollToPageElement,
      actionDisplayResolvers: copilotContext.actionDisplayResolvers,
      registerActionDisplayResolvers: copilotContext.registerActionDisplayResolvers,
      unregisterActionDisplayResolvers: copilotContext.unregisterActionDisplayResolvers,
      ...conversation
    };
    $[0] = conversation;
    $[1] = copilotContext.actionDisplayResolvers;
    $[2] = copilotContext.clearPageContext;
    $[3] = copilotContext.closeCopilotDrawer;
    $[4] = copilotContext.currentPageContext;
    $[5] = copilotContext.highlightPageElement;
    $[6] = copilotContext.isCopilotEnabled;
    $[7] = copilotContext.isCopilotFullscreenEnabled;
    $[8] = copilotContext.isCopilotOpen;
    $[9] = copilotContext.isCopilotSidenavEnabled;
    $[10] = copilotContext.openCopilotDrawer;
    $[11] = copilotContext.registerActionDisplayResolvers;
    $[12] = copilotContext.registerPageContext;
    $[13] = copilotContext.registerPageElementRefs;
    $[14] = copilotContext.scrollToPageElement;
    $[15] = copilotContext.toggleCopilot;
    $[16] = copilotContext.unregisterActionDisplayResolvers;
    $[17] = copilotContext.useCopilotFrontendTool;
    $[18] = t0;
  } else {
    t0 = $[18];
  }
  return t0;
};

/** Agent binding for the nearest {@link CopilotSurfaceProvider} surface. */
export const useSurfaceAgent = () => {
  const copilotContext = useContext(CopilotContext);
  const surface = useCopilotSurface();
  if (copilotContext == null) {
    throw new Error("No copilotContext.Provider found when calling useSurfaceAgent.");
  }
  return copilotContext.agents[surface];
};
export const useRegisterCopilotPageContext = (pageContext: CopilotPageContext | null, dependencies: readonly unknown[] = []): void => {
  const {
    registerPageContext,
    clearPageContext
  } = useCopilot();
  useEffect(() => {
    if (pageContext == null) {
      return;
    }
    registerPageContext(pageContext);
    return () => {
      clearPageContext(pageContext.pageId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageContext, registerPageContext, clearPageContext, ...dependencies]);
};
export const useRegisterCopilotActionDisplayResolvers = (resolvers: CopilotActionDisplayResolvers, dependencies: readonly unknown[] = [], ownerId = 'default'): void => {
  const {
    registerActionDisplayResolvers,
    unregisterActionDisplayResolvers
  } = useCopilot();
  useEffect(() => {
    registerActionDisplayResolvers(ownerId, resolvers);
    return () => {
      unregisterActionDisplayResolvers(ownerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, registerActionDisplayResolvers, unregisterActionDisplayResolvers, ...dependencies]);
};
export const useRegisterCopilotPageElementRefs = (pageId: string, elementRefsById: CopilotPageElementRefsById, dependencies: readonly unknown[] = []): void => {
  const {
    registerPageElementRefs
  } = useCopilot();
  useEffect(() => {
    return registerPageElementRefs(pageId, elementRefsById);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, registerPageElementRefs, ...dependencies]);
};
type AttachmentError = {
  kind: 'imageAttachment';
  reason: ImageAttachmentError | null;
} | {
  kind: 'csvAttachment';
  reason: CsvValidationReason | 'uploadFailed' | null;
};

/**
 * A validation rejection (wrong type, too large, empty, too many, …) never
 * gets a tile to show it on — see `CopilotComposerAttachments` — so it
 * surfaces as a toast instead. `uploadFailed` is excluded: that reason
 * *does* have a tile (the composer renders it as the tile's own error
 * tooltip), and toasting it here would just repeat it a second time.
 */
const useAttachmentErrorToast = (t0, dismiss) => {
  const $ = _c(7);
  const {
    kind,
    reason
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "copilot.composer"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("common", t1);
  let t2;
  let t3;
  if ($[1] !== dismiss || $[2] !== kind || $[3] !== reason || $[4] !== t) {
    t2 = () => {
      if (reason == null || reason === "uploadFailed") {
        return;
      }
      makeErrorToast(kind === "imageAttachment" ? t(`imageAttachment.errors.${reason}`, IMAGE_LIMIT_COPY_VALUES) : t(`csvAttachment.errors.${reason}`, CSV_LIMIT_COPY_VALUES));
      dismiss();
    };
    t3 = [kind, reason, dismiss, t];
    $[1] = dismiss;
    $[2] = kind;
    $[3] = reason;
    $[4] = t;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
};

/**
 * Internal dual-agent chat surface. Mounted under {@link CopilotSession}.
 */
export const CopilotChatSurface = ({
  children,
  isCopilotSidenavEnabled,
  isCopilotFullscreenEnabled,
  drawerThreadId,
  drawerChatId,
  onDrawerChatIdChange,
  onDrawerNewChat,
  onDrawerLoadChat,
  onDrawerChatHydrated,
  onDrawerTitleGenerated,
  resolveDrawerFallbackChatId,
  fullscreenThreadId,
  fullscreenChatId,
  onFullscreenChatIdChange,
  onFullscreenNewChat,
  onFullscreenLoadChat,
  onFullscreenTitleGenerated,
  initialIsOpen = false,
  urlSeededDrawerChatId = null,
  urlChatIdInvalid = false
}: {
  children: ReactNode;
  isCopilotSidenavEnabled: boolean;
  isCopilotFullscreenEnabled: boolean;
  drawerThreadId: string;
  drawerChatId: string | null;
  onDrawerChatIdChange: (chatId: string) => void;
  onDrawerNewChat: () => string;
  onDrawerLoadChat: (chatId: string) => void;
  onDrawerChatHydrated?: (chatId: string) => void;
  onDrawerTitleGenerated: (chatId: string) => void;
  /** Chat to reopen when the `?copilotChatId=` chat fails to load (the user's stored drawer chat). */
  resolveDrawerFallbackChatId?: (failedChatId: string) => string | null;
  fullscreenThreadId: string;
  fullscreenChatId: string | null;
  onFullscreenChatIdChange: (chatId: string) => void;
  onFullscreenNewChat: () => string;
  onFullscreenLoadChat: (chatId: string) => void;
  onFullscreenTitleGenerated: (chatId: string) => void;
  /** Drawer open state restored from `?copilotIsOpen=` on page load. */
  initialIsOpen?: boolean;
  /** Chat id that came from `?copilotChatId=`; a failed load of it is reported to the user. */
  urlSeededDrawerChatId?: string | null;
  /** `?copilotChatId=` was present but not a chat id; reported to the user once on load. */
  urlChatIdInvalid?: boolean;
}): ReactElement => {
  const theme = useTheme();
  const {
    t: tChat
  } = useTranslation('common', {
    keyPrefix: 'copilot'
  });
  const [isCopilotOpen, setIsCopilotOpen] = useState<boolean>(initialIsOpen);
  const [currentPageContext, setCurrentPageContext] = useState<CopilotPageContext | null>(null);
  const actionDisplayResolversByOwnerRef = useRef(new Map<string, CopilotActionDisplayResolvers>());
  const [actionDisplayResolvers, setActionDisplayResolvers] = useState<CopilotActionDisplayResolvers>({});
  const pageElementRefsByElementIdRef = useRef(new Map<string, CopilotFrontendToolElementRef[]>());
  const drawerAgent = useSurfaceAgentState('drawer');
  const fullscreenAgent = useSurfaceAgentState('fullscreen');
  // Production never rebinds the drawer, so it skips the agent lookup.
  const [drawerChatToResolve, setDrawerChatToResolve] = useState<string | null>(IS_AGENT_SWITCHER_ENABLED ? drawerChatId : null);
  const clearDrawerChatToResolve = useCallback(() => {
    setDrawerChatToResolve(null);
  }, []);
  const handleDrawerLoadChat = useCallback((chatId: string) => {
    if (IS_AGENT_SWITCHER_ENABLED) setDrawerChatToResolve(chatId);
    onDrawerLoadChat(chatId);
  }, [onDrawerLoadChat]);
  const drawer = useCopilotAgentConversation({
    agentId: toRuntimeAgentId('drawer', drawerAgent.agentName),
    activeThreadId: drawerThreadId,
    currentChatId: drawerChatId,
    onChatIdChange: onDrawerChatIdChange,
    onNewChat: onDrawerNewChat,
    onLoadChat: handleDrawerLoadChat,
    onChatHydrated: onDrawerChatHydrated,
    onTitleGenerated: onDrawerTitleGenerated,
    // Only the link chat is reported: the stored chat it falls back to is replaced silently, so the user
    // gets one toast whether they land back in their last chat or in a new one.
    onChatUnavailable: chatId => {
      if (chatId === urlSeededDrawerChatId) {
        makeErrorToast(tChat('chatNotFound'));
      }
    },
    resolveFallbackChatId: resolveDrawerFallbackChatId,
    enableDatasourceSession: false,
    enableKnowledgeSourceSession: true,
    // The drawer agent (alvie_copilot) has no CSV/data-source routing — it directs
    // users to Alvie AI Pro for deep analysis, so the attach button lives only there.
    enableCsvAttachment: false,
    enableImageAttachment: false,
    sourceSelectionSurface: 'drawer'
  });
  const fullscreen = useCopilotAgentConversation({
    agentId: toRuntimeAgentId('fullscreen', fullscreenAgent.agentName),
    activeThreadId: fullscreenThreadId,
    currentChatId: fullscreenChatId,
    onChatIdChange: onFullscreenChatIdChange,
    onNewChat: onFullscreenNewChat,
    onLoadChat: onFullscreenLoadChat,
    onTitleGenerated: onFullscreenTitleGenerated,
    enableDatasourceSession: true,
    enableCsvAttachment: true,
    enableImageAttachment: true,
    sourceSelectionSurface: 'fullscreen'
  });
  useDrawerChatAgent({
    currentChatId: drawerChatId,
    chatToResolve: drawerChatToResolve,
    clearChatToResolve: clearDrawerChatToResolve,
    agentName: drawerAgent.agentName,
    preferredAgentName: drawerAgent.preferredAgentName,
    setAgentName: drawerAgent.setAgentName,
    loadChat: drawer.loadChat
  });
  useEffect(() => {
    return () => {
      resetCopilotFrontendToolElementEffectState();
    };
  }, []);

  // Page-tool highlights track the drawer (page agent) message stream.
  useCopilotFrontendToolElementEffects(drawer.messages);
  useAttachmentErrorToast({
    kind: 'imageAttachment',
    reason: drawer.imageAttachmentErrorReason
  }, drawer.dismissImageAttachmentError);
  useAttachmentErrorToast({
    kind: 'csvAttachment',
    reason: drawer.csvAttachmentErrorReason
  }, drawer.dismissCsvAttachmentError);
  useAttachmentErrorToast({
    kind: 'imageAttachment',
    reason: fullscreen.imageAttachmentErrorReason
  }, fullscreen.dismissImageAttachmentError);
  useAttachmentErrorToast({
    kind: 'csvAttachment',
    reason: fullscreen.csvAttachmentErrorReason
  }, fullscreen.dismissCsvAttachmentError);
  const agentPageContext = useMemo(() => currentPageContext == null ? null : toCopilotAgentPageContext(currentPageContext), [currentPageContext]);
  useCopilotGlobalAgentContext();
  useCopilotAgentContext({
    description: 'The page the user is currently viewing.',
    value: (agentPageContext ?? {}) as unknown as JsonSerializable,
    available: agentPageContext != null ? 'enabled' : 'disabled'
  });
  useCopilotKitFrontendTool({
    name: '*',
    available: false,
    handler: async (wildcardArgs: Record<string, unknown>) => {
      const toolName = String(wildcardArgs.toolName ?? 'unknown');
      log.warn({
        toolName
      }, 'unhandled frontend tool');
      return `Error: tool "${toolName}" is not available on the current page.`;
    }
  });
  useCopilotUrlStateMirror({
    isOpen: isCopilotOpen,
    chatId: drawerChatId
  });
  useEffect(() => {
    if (urlChatIdInvalid) {
      makeErrorToast(tChat('chatNotFound'));
    }
    // Report once: the URL only seeds state on load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const openCopilotDrawer = useCallback(() => {
    if (!isCopilotSidenavEnabled) {
      return;
    }
    setIsCopilotOpen(true);
  }, [isCopilotSidenavEnabled]);
  const closeCopilotDrawer = useCallback(() => {
    setIsCopilotOpen(false);
  }, []);
  const toggleCopilot = useCallback(() => {
    if (!isCopilotSidenavEnabled) {
      return;
    }
    setIsCopilotOpen(prev => !prev);
  }, [isCopilotSidenavEnabled]);
  const clearPageContext = useCallback((pageId: string) => {
    setCurrentPageContext(registeredContext => registeredContext?.pageId === pageId ? null : registeredContext);
  }, []);
  const registerActionDisplayResolvers = useCallback((ownerId: string, resolvers: CopilotActionDisplayResolvers) => {
    actionDisplayResolversByOwnerRef.current.set(ownerId, resolvers);
    setActionDisplayResolvers(mergeCopilotActionDisplayResolvers(actionDisplayResolversByOwnerRef.current));
  }, []);
  const unregisterActionDisplayResolvers = useCallback((ownerId: string) => {
    actionDisplayResolversByOwnerRef.current.delete(ownerId);
    setActionDisplayResolvers(mergeCopilotActionDisplayResolvers(actionDisplayResolversByOwnerRef.current));
  }, []);
  const registerPageElementRefs = useCallback((pageId: string, elementRefsById: CopilotPageElementRefsById): (() => void) => {
    Object.entries(elementRefsById).forEach(([elementId, elementRefs]) => {
      if (elementRefs != null) {
        pageElementRefsByElementIdRef.current.set(elementId, elementRefs);
      }
    });
    return () => {
      Object.keys(elementRefsById).forEach(elementId => {
        pageElementRefsByElementIdRef.current.delete(elementId);
      });
    };
  }, []);
  const highlightPageElement = useCallback((elementId: string): boolean => {
    return pulseCopilotFrontendToolElementRefs(pageElementRefsByElementIdRef.current.get(elementId), `highlight-${elementId}-${Date.now()}`, theme);
  }, [theme]);
  const scrollToPageElement = useCallback((elementId: string): void => {
    const element = pageElementRefsByElementIdRef.current.get(elementId)?.[0]?.current;
    if (element != null) {
      scrollCopilotElementIntoView(element);
    }
  }, []);
  const toConversationFields = (conversation: ReturnType<typeof useCopilotAgentConversation>): CopilotConversationFields => ({
    messages: conversation.messages,
    sendUserMessage: conversation.sendUserMessage,
    sendToolResult: conversation.sendToolResult,
    isLoading: conversation.isLoading,
    isHydratingChat: conversation.isHydratingChat,
    chatLoadFailed: conversation.chatLoadFailed,
    retryChatLoad: conversation.retryChatLoad,
    stopGeneration: conversation.stopGeneration,
    currentChatId: conversation.currentChatId,
    createNewChat: conversation.createNewChat,
    loadChat: conversation.loadChat,
    selectedDatasourceIds: conversation.selectedDatasourceIds,
    selectedEntityTypes: conversation.selectedEntityTypes,
    selectedCirrusSource: conversation.selectedCirrusSource,
    hasSelectedDatasource: conversation.hasSelectedDatasource,
    setSelectedDatasources: conversation.setSelectedDatasources,
    setSelectedCirrusSource: conversation.setSelectedCirrusSource,
    clearSelectedDatasource: conversation.clearSelectedDatasource,
    reconcileDatasourceWithAvailableOptions: conversation.reconcileDatasourceWithAvailableOptions,
    selectedKnowledgeSourceIds: conversation.selectedKnowledgeSourceIds,
    hasSelectedKnowledgeSource: conversation.hasSelectedKnowledgeSource,
    setSelectedKnowledgeSources: conversation.setSelectedKnowledgeSources,
    clearSelectedKnowledgeSource: conversation.clearSelectedKnowledgeSource,
    reconcileKnowledgeSourcesWithAvailableCorpora: conversation.reconcileKnowledgeSourcesWithAvailableCorpora,
    attachedCsvFile: conversation.attachedCsvFile,
    pendingCsvFilename: conversation.pendingCsvFilename,
    csvAttachmentStatus: conversation.csvAttachmentStatus,
    csvAttachmentErrorReason: conversation.csvAttachmentErrorReason,
    isCsvUploading: conversation.isCsvUploading,
    attachCsvFile: conversation.attachCsvFile,
    retryCsvAttachment: conversation.retryCsvAttachment,
    clearCsvAttachment: conversation.clearCsvAttachment,
    dismissCsvAttachmentError: conversation.dismissCsvAttachmentError,
    imageAttachments: conversation.imageAttachments,
    imageAttachmentErrorReason: conversation.imageAttachmentErrorReason,
    isImageUploading: conversation.isImageUploading,
    attachImageFiles: conversation.attachImageFiles,
    removeImageAttachment: conversation.removeImageAttachment,
    retryImageAttachment: conversation.retryImageAttachment,
    dismissImageAttachmentError: conversation.dismissImageAttachmentError,
    justSentImagePreviewByGcsUri: conversation.justSentImagePreviewByGcsUri
  });
  const contextValue: CopilotInternalContextValue = useMemo(() => ({
    isCopilotEnabled: true,
    isCopilotSidenavEnabled,
    isCopilotFullscreenEnabled,
    isCopilotOpen: isCopilotSidenavEnabled && isCopilotOpen,
    openCopilotDrawer,
    closeCopilotDrawer,
    toggleCopilot,
    useCopilotFrontendTool,
    currentPageContext,
    registerPageContext: setCurrentPageContext,
    clearPageContext,
    registerPageElementRefs,
    highlightPageElement,
    scrollToPageElement,
    actionDisplayResolvers,
    registerActionDisplayResolvers,
    unregisterActionDisplayResolvers,
    conversations: {
      drawer: toConversationFields(drawer),
      fullscreen: toConversationFields(fullscreen)
    },
    agents: {
      drawer: drawerAgent,
      fullscreen: fullscreenAgent
    }
  }), [actionDisplayResolvers, clearPageContext, closeCopilotDrawer, currentPageContext, drawer, drawerAgent, fullscreen, fullscreenAgent, highlightPageElement, isCopilotFullscreenEnabled, isCopilotOpen, isCopilotSidenavEnabled, openCopilotDrawer, registerActionDisplayResolvers, registerPageElementRefs, scrollToPageElement, toggleCopilot, unregisterActionDisplayResolvers]);
  return <CopilotContext.Provider value={contextValue}>
      {isCopilotFullscreenEnabled ? <BackendLongRunningToolStubs /> : null}
      {children}
    </CopilotContext.Provider>;
};
const CopilotSession = t0 => {
  const $ = _c(47);
  const {
    userId,
    userEmail,
    clientId,
    seedChatIdFromUrl,
    isCopilotSidenavEnabled,
    isCopilotFullscreenEnabled,
    onUnrecoverableError,
    children
  } = t0;
  let t1;
  if ($[0] !== seedChatIdFromUrl) {
    t1 = () => {
      const parsed = parseCopilotUrlState(globalThis.window === undefined ? "" : window.location.search);
      return seedChatIdFromUrl ? parsed : {
        ...parsed,
        chatId: null,
        chatIdInvalid: false
      };
    };
    $[0] = seedChatIdFromUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [urlState] = useState(t1);
  let t2;
  if ($[2] !== clientId || $[3] !== urlState.chatId || $[4] !== urlState.chatIdInvalid || $[5] !== userId) {
    t2 = {
      userId,
      clientId,
      surface: "drawer",
      onError: noop,
      urlChatId: urlState.chatId,
      urlChatIdInvalid: urlState.chatIdInvalid
    };
    $[2] = clientId;
    $[3] = urlState.chatId;
    $[4] = urlState.chatIdInvalid;
    $[5] = userId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const drawerSession = useCopilotSession(t2);
  let t3;
  if ($[7] !== clientId || $[8] !== userId) {
    t3 = {
      userId,
      clientId,
      surface: "fullscreen",
      onError: noop
    };
    $[7] = clientId;
    $[8] = userId;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const fullscreenSession = useCopilotSession(t3);
  let t4;
  if ($[10] !== userId) {
    t4 = userId.toString();
    $[10] = userId;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== clientId) {
    t5 = clientId.toString();
    $[12] = clientId;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5 || $[16] !== userEmail) {
    t6 = {
      "X-Alvie-User-Id": t4,
      "X-Alvie-Client-Id": t5,
      "X-Alvie-User-Email": userEmail
    };
    $[14] = t4;
    $[15] = t5;
    $[16] = userEmail;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const headers = t6;
  let t7;
  if ($[18] !== drawerSession || $[19] !== fullscreenSession || $[20] !== onUnrecoverableError) {
    t7 = error => {
      drawerSession.onError(error);
      fullscreenSession.onError(error);
      onUnrecoverableError(error);
    };
    $[18] = drawerSession;
    $[19] = fullscreenSession;
    $[20] = onUnrecoverableError;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const handleError = t7;
  const handleDrawerChatIdChange = drawerSession.onChatIdChange;
  const handleDrawerNewChat = drawerSession.onNewChat;
  const handleDrawerLoadChat = drawerSession.onLoadChat;
  const handleDrawerChatHydrated = drawerSession.onChatHydrated;
  const handleDrawerTitleGenerated = drawerSession.onTitleGenerated;
  const resolveDrawerFallbackChatId = drawerSession.resolveFallbackChatId;
  const handleFullscreenChatIdChange = fullscreenSession.onChatIdChange;
  const handleFullscreenNewChat = fullscreenSession.onNewChat;
  const handleFullscreenLoadChat = fullscreenSession.onLoadChat;
  const handleFullscreenTitleGenerated = fullscreenSession.onTitleGenerated;
  let t8;
  if ($[22] !== children || $[23] !== drawerSession.chatId || $[24] !== drawerSession.threadId || $[25] !== fullscreenSession.chatId || $[26] !== fullscreenSession.threadId || $[27] !== handleDrawerChatHydrated || $[28] !== handleDrawerChatIdChange || $[29] !== handleDrawerLoadChat || $[30] !== handleDrawerNewChat || $[31] !== handleDrawerTitleGenerated || $[32] !== handleFullscreenChatIdChange || $[33] !== handleFullscreenLoadChat || $[34] !== handleFullscreenNewChat || $[35] !== handleFullscreenTitleGenerated || $[36] !== isCopilotFullscreenEnabled || $[37] !== isCopilotSidenavEnabled || $[38] !== resolveDrawerFallbackChatId || $[39] !== urlState.chatId || $[40] !== urlState.chatIdInvalid || $[41] !== urlState.isOpen) {
    t8 = <CopilotChatSurface isCopilotSidenavEnabled={isCopilotSidenavEnabled} isCopilotFullscreenEnabled={isCopilotFullscreenEnabled} initialIsOpen={urlState.isOpen} urlSeededDrawerChatId={urlState.chatId} urlChatIdInvalid={urlState.chatIdInvalid} drawerThreadId={drawerSession.threadId} drawerChatId={drawerSession.chatId} onDrawerChatIdChange={handleDrawerChatIdChange} onDrawerNewChat={handleDrawerNewChat} onDrawerLoadChat={handleDrawerLoadChat} onDrawerChatHydrated={handleDrawerChatHydrated} onDrawerTitleGenerated={handleDrawerTitleGenerated} resolveDrawerFallbackChatId={resolveDrawerFallbackChatId} fullscreenThreadId={fullscreenSession.threadId} fullscreenChatId={fullscreenSession.chatId} onFullscreenChatIdChange={handleFullscreenChatIdChange} onFullscreenNewChat={handleFullscreenNewChat} onFullscreenLoadChat={handleFullscreenLoadChat} onFullscreenTitleGenerated={handleFullscreenTitleGenerated}>{children}</CopilotChatSurface>;
    $[22] = children;
    $[23] = drawerSession.chatId;
    $[24] = drawerSession.threadId;
    $[25] = fullscreenSession.chatId;
    $[26] = fullscreenSession.threadId;
    $[27] = handleDrawerChatHydrated;
    $[28] = handleDrawerChatIdChange;
    $[29] = handleDrawerLoadChat;
    $[30] = handleDrawerNewChat;
    $[31] = handleDrawerTitleGenerated;
    $[32] = handleFullscreenChatIdChange;
    $[33] = handleFullscreenLoadChat;
    $[34] = handleFullscreenNewChat;
    $[35] = handleFullscreenTitleGenerated;
    $[36] = isCopilotFullscreenEnabled;
    $[37] = isCopilotSidenavEnabled;
    $[38] = resolveDrawerFallbackChatId;
    $[39] = urlState.chatId;
    $[40] = urlState.chatIdInvalid;
    $[41] = urlState.isOpen;
    $[42] = t8;
  } else {
    t8 = $[42];
  }
  let t9;
  if ($[43] !== handleError || $[44] !== headers || $[45] !== t8) {
    t9 = <CopilotKit runtimeUrl="/api/copilot" agent={ALVIE_COPILOT_AGENT_ID} publicLicenseKey="ck_pub_a9679d33711dd05032aabac187785af8" headers={headers} onError={handleError}>{t8}</CopilotKit>;
    $[43] = handleError;
    $[44] = headers;
    $[45] = t8;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  return t9;
};

/**
 * Public entry point — mount once at the app root.
 */
export const CopilotProvider = ({
  children
}: {
  children: ReactNode;
}): ReactElement => {
  const {
    t
  } = useTranslation('common', {
    keyPrefix: 'copilot'
  });
  const {
    value: isCopilotSidenavEnabled
  } = useFeatureFlag(FEATURE_FLAGS.copilotSidenav);
  const {
    value: isCopilotFullscreenEnabled
  } = useFeatureFlag(FEATURE_FLAGS.copilot);
  const isCopilotEnabled = isCopilotSidenavEnabled || isCopilotFullscreenEnabled;
  const {
    currentUser
  } = useAuth();
  const {
    currentClient
  } = useCurrentClient();
  // Session the URL chat id was meant for: the first one of the page load. `null` once the user or
  // client has changed, since from then on the URL only holds ids the mirror wrote for an earlier session.
  const urlSeedSessionKeyRef = useRef<string | null | undefined>(undefined);
  const handleError = useCallback((error: CopilotErrorEvent) => {
    if (error.error.code === 'abort') {
      return;
    }

    // CopilotKit reports runtime failures here and never dispatches client-error.
    if (isCopilotSessionMissing(error)) {
      dispatchClientError(new ApiClientError({
        message: SESSION_EXPIRED_MESSAGE,
        code: AUTH_SESSION_MISSING,
        status_code: HttpStatusCodes.UNAUTHORIZED,
        endpoint: '/api/copilot'
      }));
      return;
    }

    // A 400 from Flow is a rejected request payload (e.g. a `cirrus_source` Flow will
    // not accept). It fails identically on retry, so surface the reason rather than
    // hiding it behind the generic message — and never retry or drop the data source.
    const badRequestMessage = getCopilotBadRequestMessage(error);
    if (badRequestMessage != null) {
      log.warn({
        message: badRequestMessage
      }, 'copilot run rejected as a bad request');
      makeErrorToast(t('copilotErrorDataSource', {
        message: badRequestMessage
      }));
      return;
    }
    makeErrorToast(t('copilotErrorGeneric'));
  }, [t]);
  if (!isCopilotEnabled) {
    return <CopilotContext.Provider value={disabledCopilotContextValue}>{children}</CopilotContext.Provider>;
  }
  if (currentUser?.id != null && Boolean(currentClient.id)) {
    const sessionKey = `${currentUser.id}-${currentClient.id}`;
    if (urlSeedSessionKeyRef.current === undefined) {
      urlSeedSessionKeyRef.current = sessionKey;
    } else if (urlSeedSessionKeyRef.current !== sessionKey) {
      urlSeedSessionKeyRef.current = null;
    }
    return <CopilotSession key={sessionKey} userId={currentUser.id} userEmail={currentUser.email} clientId={currentClient.id} seedChatIdFromUrl={urlSeedSessionKeyRef.current === sessionKey} isCopilotSidenavEnabled={isCopilotSidenavEnabled} isCopilotFullscreenEnabled={isCopilotFullscreenEnabled} onUnrecoverableError={handleError}>
        {children}
      </CopilotSession>;
  }
  return <CopilotContext.Provider value={disabledCopilotContextValue}>{children}</CopilotContext.Provider>;
};